import { useEffect, useState } from 'react';
import { Plus, Trash2, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { adminApi, publicApi } from '../../lib/api';

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, review: null });
  
  const [newReview, setNewReview] = useState({
    name: '',
    country: '',
    rating: '5',
    comment: '',
    date: ''
  });

  const fetchReviews = async () => {
    try {
      const response = await publicApi.getReviews();
      setReviews(response.data);
    } catch (error) {
      toast.error('Error al cargar reseñas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const handleCreate = async () => {
    try {
      await adminApi.createReview({
        ...newReview,
        rating: parseInt(newReview.rating)
      });
      toast.success('Reseña creada');
      setDialogOpen(false);
      setNewReview({ name: '', country: '', rating: '5', comment: '', date: '' });
      fetchReviews();
    } catch (error) {
      toast.error('Error al crear reseña');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.review) return;
    
    try {
      await adminApi.deleteReview(deleteDialog.review.id);
      toast.success('Reseña eliminada');
      setDeleteDialog({ open: false, review: null });
      fetchReviews();
    } catch (error) {
      toast.error('Error al eliminar reseña');
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? 'fill-amber-400 text-amber-400' : 'text-stone-200'}`}
      />
    ));
  };

  return (
    <div data-testid="admin-reviews">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-serif text-stone-900">Reseñas</h1>
          <p className="text-stone-500 mt-1">Gestiona las reseñas del sitio</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-xl gap-2" data-testid="admin-new-review-btn">
              <Plus className="w-4 h-4" />
              Nueva Reseña
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Agregar Reseña</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Nombre</Label>
                <Input
                  value={newReview.name}
                  onChange={(e) => setNewReview(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Nombre del cliente"
                  className="mt-2"
                />
              </div>
              <div>
                <Label>País</Label>
                <Input
                  value={newReview.country}
                  onChange={(e) => setNewReview(prev => ({ ...prev, country: e.target.value }))}
                  placeholder="Ej: España"
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Calificación</Label>
                <Select 
                  value={newReview.rating} 
                  onValueChange={(v) => setNewReview(prev => ({ ...prev, rating: v }))}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[5, 4, 3, 2, 1].map(n => (
                      <SelectItem key={n} value={String(n)}>
                        {n} {n === 1 ? 'estrella' : 'estrellas'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Fecha</Label>
                <Input
                  value={newReview.date}
                  onChange={(e) => setNewReview(prev => ({ ...prev, date: e.target.value }))}
                  placeholder="Ej: Enero 2024"
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Comentario</Label>
                <Textarea
                  value={newReview.comment}
                  onChange={(e) => setNewReview(prev => ({ ...prev, comment: e.target.value }))}
                  placeholder="El comentario de la reseña..."
                  className="mt-2"
                  rows={4}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleCreate} disabled={!newReview.name || !newReview.comment}>
                  Crear Reseña
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Reviews Grid */}
      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-40 bg-stone-100 rounded-xl skeleton" />
          ))}
        </div>
      ) : reviews.length === 0 ? (
        <Card className="p-12 text-center">
          <p className="text-stone-500 mb-4">No hay reseñas</p>
          <Button onClick={() => setDialogOpen(true)}>Crear primera reseña</Button>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reviews.map((review) => (
            <Card key={review.id} className="relative group">
              <CardContent className="pt-6">
                {/* Delete button */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => setDeleteDialog({ open: true, review })}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>

                {/* Header */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-primary font-bold">
                      {review.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-stone-900">{review.name}</p>
                    <p className="text-xs text-stone-500">{review.country}</p>
                  </div>
                </div>

                {/* Stars */}
                <div className="flex gap-1 mb-3">
                  {renderStars(review.rating)}
                </div>

                {/* Comment */}
                <p className="text-sm text-stone-600 line-clamp-3">
                  "{review.comment}"
                </p>

                {/* Date */}
                <p className="text-xs text-stone-400 mt-3">{review.date}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, review: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta reseña?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
